// CancelInvoiceModal.jsx
import React, { useState, useEffect } from "react";
import {
    Modal,
    Box,
    Typography,
    Divider,
    TextField,
    Button,
    Alert,
    CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ProjectService from "../../services/ProjectService";
import { useTranslation } from "react-i18next";
import { useToast } from "../../contexts/ToastContext";

export default function CancelInvoiceModal({
                                               isCancelInvoiceModalOpen,
                                               onClose,
                                               onSuccess,
                                               invoice = {}, // { id, displayName, name, totalPrice, dueDate }
                                           }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [reason, setReason] = useState("");
    const [touched, setTouched] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isCancelInvoiceModalOpen) {
            setReason("");
            setTouched(false);
        }
    }, [isCancelInvoiceModalOpen]);

    const amount =
        typeof invoice?.totalPrice === "number"
            ? invoice.totalPrice.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
            : "-";

    const hasError = touched && reason.trim().length === 0;

    const handleConfirm = async () => {
        setTouched(true);
        if (reason.trim().length === 0) return;
        await handleCancelInvoiceEntry();
    };

    const handleCancelInvoiceEntry = async () => {
        try {
            setIsSubmitting(true);
            const requestData = { cancellationReason: reason.trim() };
            await ProjectService.cancelInvoice(invoice.id, requestData);
            showToast(t("cancelInvoiceModal.toastSuccess"), 'success');
            onClose();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error("Failed to cancel invoice:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={isCancelInvoiceModalOpen} onClose={onClose}>
            <Box
                sx={{
                    width: 520,
                    maxWidth: 'calc(100vw - 32px)',
                    m: "10% auto",
                    p: 3,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    boxShadow: 24,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <Typography variant="h6">
                    {t("cancelInvoiceModal.title")}
                </Typography>
                <Divider />

                <Alert severity="warning">
                    <strong>{t("cancelInvoiceModal.warningTitle")}</strong>{" "}
                    {t("cancelInvoiceModal.warningIrreversible")}
                    <br />
                    {t("cancelInvoiceModal.warningNote")}
                </Alert>

                <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {t("cancelInvoiceModal.invoiceLabel")}
                    </Typography>
                    <Typography variant="subtitle1">
                        {invoice?.displayName || invoice?.name || "—"}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        {t("cancelInvoiceModal.amountLabel")}
                    </Typography>
                    <Typography variant="subtitle1">CHF {amount}</Typography>

                    <Typography variant="body2" color="text.secondary">
                        {t("cancelInvoiceModal.creationDateLabel")}
                    </Typography>
                    <Typography variant="subtitle1">
                        {invoice?.dueDate
                            ? new Date(invoice.dueDate).toLocaleDateString("de-CH", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                            })
                            : "—"}
                    </Typography>
                </Box>

                <TextField
                    label={t("cancelInvoiceModal.reasonLabel")}
                    placeholder={t("cancelInvoiceModal.reasonPlaceholder")}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    onBlur={() => setTouched(true)}
                    multiline
                    rows={4}
                    fullWidth
                    error={hasError}
                    helperText={hasError ? t("cancelInvoiceModal.reasonRequired") : " "}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}>
                    <Button variant="outlined" color="primary" onClick={onClose}>
                        {t("common.dismiss")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirm}
                        disabled={isSubmitting || reason.trim().length === 0}
                        endIcon={
                            isSubmitting ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <CheckIcon />
                            )
                        }
                    >
                        {t("cancelInvoiceModal.confirmButton")}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
